import { useState } from 'react';
import {
  Box, Typography, Button, Popover, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Divider, alpha, useTheme, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { AccountBalanceWallet, Stars, Sync } from '@mui/icons-material';
import { formatMoney } from '../../utils/currency';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';

export type AdjustMode = 'credit' | 'debit';

interface BalanceAdjustPopoverProps {
  type: 'wallet' | 'loyalty';
  displayValue: string;
  disabled?: boolean;
  loading?: boolean;
  onRefresh?: () => void;
  onApply: (mode: AdjustMode, amount: number, note: string) => void;
}

const QUICK_AMOUNTS = {
  wallet: [100, 500, 1000, 5000],
  loyalty: [25, 50, 100, 250],
};

export default function BalanceAdjustPopover({
  type, displayValue, disabled, loading, onRefresh, onApply,
}: BalanceAdjustPopoverProps) {
  const theme = useTheme();
  const { fieldLabel } = useTenantCurrency();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [mode, setMode] = useState<AdjustMode>('credit');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const open = Boolean(anchor);
  const isWallet = type === 'wallet';
  const Icon = isWallet ? AccountBalanceWallet : Stars;
  const accent = isWallet ? theme.palette.primary.main : theme.palette.secondary.main;

  const handleClose = () => {
    setAnchor(null);
    setAmount('');
    setNote('');
    setMode('credit');
  };

  const handleApply = () => {
    const num = Number(amount);
    if (!num || num <= 0) return;
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    const num = Number(amount);
    if (!num || num <= 0) return;
    onApply(mode, num, note || (mode === 'credit' ? 'Credit adjustment' : 'Debit adjustment'));
    setConfirmOpen(false);
    handleClose();
  };

  return (
    <>
      <Box
        onClick={(e) => { if (!disabled) { e.stopPropagation(); setAnchor(e.currentTarget); } }}
        sx={{
          cursor: disabled ? 'default' : 'pointer',
          px: 1.25,
          py: 0.75,
          borderRadius: 1.5,
          border: 1,
          borderColor: disabled ? 'divider' : alpha(accent, 0.35),
          bgcolor: disabled ? 'action.hover' : alpha(accent, 0.06),
          minWidth: 120,
          transition: 'border-color 0.2s, box-shadow 0.2s',
          '&:hover': disabled ? {} : {
            borderColor: accent,
            boxShadow: `0 0 0 1px ${alpha(accent, 0.2)}`,
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Icon sx={{ fontSize: 16, color: accent, opacity: 0.9 }} />
          <Typography variant="body2" fontWeight={700} noWrap>
            {displayValue}
          </Typography>
        </Stack>
        {!disabled && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
            Tap to adjust
          </Typography>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 280, borderRadius: 2 } } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" fontWeight={700}>
            {isWallet ? 'Wallet adjustment' : 'Loyalty adjustment'}
          </Typography>
          {onRefresh && (
            <Button size="small" startIcon={<Sync fontSize="small" />} onClick={onRefresh} disabled={loading}>
              Sync
            </Button>
          )}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
          Current: {displayValue}
        </Typography>

        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={mode}
          onChange={(_, v) => v && setMode(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="credit" color="success">Credit (+)</ToggleButton>
          <ToggleButton value="debit" color="warning">Debit (−)</ToggleButton>
        </ToggleButtonGroup>

        <TextField
          autoFocus
          fullWidth
          size="small"
          type="number"
          label={isWallet ? fieldLabel('Amount') : 'Points'}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.5}>
          {QUICK_AMOUNTS[type].map((q) => (
            <Button
              key={q}
              size="small"
              variant="outlined"
              onClick={() => setAmount(String(q))}
              sx={{ minWidth: 0, px: 1.25, py: 0.25, fontSize: 12 }}
            >
              {isWallet ? `${formatMoney(q)}` : q}
            </Button>
          ))}
        </Stack>

        <TextField
          fullWidth
          size="small"
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          multiline
          rows={2}
          sx={{ mb: 2 }}
        />

        <Divider sx={{ mb: 1.5 }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={handleClose}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            color={mode === 'credit' ? 'success' : 'warning'}
            disabled={loading || !amount || Number(amount) <= 0}
            onClick={handleApply}
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            Apply {mode === 'credit' ? 'credit' : 'debit'}
          </Button>
        </Stack>
      </Popover>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm {isWallet ? 'wallet' : 'loyalty'} adjustment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Please confirm this {mode === 'credit' ? 'credit' : 'debit'} before applying.
          </Typography>
          <Typography variant="body1" fontWeight={700}>
            {mode === 'credit' ? '+' : '−'}{isWallet ? `${formatMoney(amount)}` : `${amount} pts`}
          </Typography>
          {note && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              Note: {note}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color={mode === 'credit' ? 'success' : 'warning'} onClick={handleConfirm} disabled={loading}>
            Confirm & apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
